import React, { useMemo } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { Offer } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { EPopupSubType, SortingOffersValue } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getConvertedPrice } from 'utils/getConvertedPrice';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import SortableHeader from 'components/SortableHeader/SortableHeader';

import { IOfferColumnsBaseProps } from '../../../Offer.types';
import { renderOfferStatusChip } from '../../../Offer.utils';

const dayjsUTC = dayjs.extend(utc);

interface IUsePopupsColumnsProps extends IOfferColumnsBaseProps {
  isPricingsLoading: boolean;
  pricingsResult: any;
}

interface IUsePopupsColumnsReturn {
  columns: ColumnDef<Offer>[];
}

/**
 * Hook that builds the column definitions for the Popups table.
 *
 * @example
 * const { columns } = usePopupsColumns({
 *   sorting, updateSorting, isLoading, actions, permissions,
 *   isPricingsLoading, pricingsResult
 * });
 */
export const usePopupsColumns = ({
  sorting,
  updateSorting,
  isLoading,
  actions,
  permissions,
  isPricingsLoading,
  pricingsResult
}: IUsePopupsColumnsProps): IUsePopupsColumnsReturn => {
  const columns = useMemo((): ColumnDef<Offer>[] => {
    return [
      {
        accessorKey: 'offerId',
        header: () => (
          <SortableHeader
            label="ID"
            sortValue={SortingOffersValue.ID}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'publisherOfferId',
        header: () => (
          <SortableHeader
            label="External ID"
            sortValue={SortingOffersValue.PUBLISHER_OFFER_ID}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'name',
        header: () => (
          <SortableHeader
            label="Name"
            sortValue={SortingOffersValue.NAME}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'subType',
        header: 'Type',
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'schedule',
        header: 'Schedule',
        cell: ({ row }: any) => {
          if (row.original.subType === EPopupSubType.DAILY_BONUS) {
            const filteredProducts =
              row.original.productsSequence?.filter(
                (item: any) => item.products.length > 0
              ) || [];
            return filteredProducts.length > 0
              ? `${filteredProducts.length} days`
              : '-';
          }
          return '-';
        }
      },
      {
        accessorKey: 'price',
        header: () => (
          <SortableHeader
            label="Price"
            sortValue={SortingOffersValue.PRICE}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ row }: any) => {
          if (row.original.subType === EPopupSubType.DAILY_BONUS) {
            return '-';
          }
          if (isLoading || isPricingsLoading) {
            return '';
          }
          return getConvertedPrice(row.original, pricingsResult);
        }
      },
      {
        accessorKey: 'active',
        header: () => (
          <SortableHeader
            label="Status"
            sortValue={SortingOffersValue.STATUS}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        accessorFn: (row: any) => {
          return row.active ? 'Active' : 'Inactive';
        },
        cell: ({ row }: any) => {
          return renderOfferStatusChip(row.original);
        }
      },
      {
        accessorKey: 'createdAt',
        header: () => (
          <SortableHeader
            label="Created At"
            sortValue={SortingOffersValue.CREATED_AT}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ row }: any) => {
          if (isLoading) {
            return '';
          }
          return (
            <span className="whitespace-nowrap">
              {dayjsUTC.utc(row.original.createdAt).format(DATE_TIME_FORMAT)}
            </span>
          );
        }
      },
      {
        accessorKey: 'actions',
        header: '',
        maxSize: 60,
        minSize: 60,
        size: 60,
        cell: ({ row }: any) => (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={row.original}
              actions={[
                {
                  label: 'Edit',
                  handler: () => {
                    actions.onEdit(row.original.offerId);
                  },
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers,
                  hidden: !permissions.canManageOffers
                },
                {
                  label: 'Edit',
                  handler: () => {
                    actions.onLimitedEdit(row.original.offerId);
                  },
                  disabled:
                    !permissions.canUserEdit ||
                    !permissions.canUpdateStatusDesign,
                  hidden:
                    !permissions.canUpdateStatusDesign ||
                    permissions.canManageOffers
                },
                {
                  label: row.original.active ? 'Deactivate' : 'Activate',
                  handler: () =>
                    actions.onToggleStatus(
                      row.original.offerId,
                      row.original.active
                    ),
                  disabled: !permissions.canUserEdit
                },
                {
                  label: 'Duplicate',
                  handler: () => actions.onDuplicate(row.original.offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers
                },
                {
                  label: 'Delete',
                  handler: () => actions.onDelete(row.original.offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers
                }
              ]}
            />
          </div>
        )
      }
    ];
  }, [
    sorting,
    updateSorting,
    isLoading,
    isPricingsLoading,
    pricingsResult,
    actions,
    permissions
  ]);

  return { columns };
};
